import React, { useState, useEffect } from 'react';
import { 
  BellIcon, 
  XMarkIcon,
  CheckIcon,
  ClockIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const NotificationPopup = ({ 
  notification, 
  onClose, 
  onMarkAsRead,
  autoClose = true,
  duration = 5000 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  // Auto-close dopo la durata specificata
  useEffect(() => {
    if (autoClose && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [autoClose, duration]);

  // Gestisce la chiusura con animazione
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, 300);
  };

  // Gestisce il click per segnare come letta
  const handleMarkAsRead = () => {
    if (onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
    handleClose();
  };

  // Formatta la data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formatta il messaggio con newlines
  const formatMessage = (message) => {
    if (!message) return '';
    return message.split('\n').map((line, index) => (
      <span key={index}>
        {line}
        {index < message.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  // Icona per tipo notifica
  const getTypeIcon = (type) => {
    switch (type) {
      case 'announcement':
        return <BellIcon className="h-5 w-5 text-blue-500" />;
      case 'message':
        return <UserIcon className="h-5 w-5 text-green-500" />;
      case 'course':
        return <ClockIcon className="h-5 w-5 text-purple-500" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  // Colore per tipo notifica
  const getTypeColor = (type) => {
    switch (type) {
      case 'announcement':
        return 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20';
      case 'message':
        return 'border-l-green-500 bg-green-50 dark:bg-green-900/20';
      case 'course':
        return 'border-l-purple-500 bg-purple-50 dark:bg-purple-900/20';
      default:
        return 'border-l-gray-500 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`
      fixed top-4 right-4 w-80 max-w-sm
      bg-white dark:bg-gray-800
      border border-gray-200 dark:border-gray-700
      rounded-lg shadow-lg
      z-50
      transform transition-all duration-300 ease-in-out
      ${isClosing 
        ? 'translate-x-full opacity-0' 
        : 'translate-x-0 opacity-100'
      }
      ${getTypeColor(notification.type)}
      border-l-4
    `}>
      {/* Header */}
      <div className="p-4 pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            {getTypeIcon(notification.type)}
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              {notification.title}
            </h4>
          </div>
          
          <button
            onClick={handleClose}
            className="
              p-1 rounded-full
              hover:bg-gray-200 dark:hover:bg-gray-600
              transition-colors duration-200
            "
          >
            <XMarkIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      </div>

      {/* Messaggio */}
      <div className="px-4 pb-2">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          {formatMessage(notification.message)}
        </p>
      </div>

      {/* Footer */}
      <div className="px-4 pb-4">
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {formatDate(notification.created_at)}
          </div>
          
          {/* Azioni */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleMarkAsRead}
              className="
                px-3 py-1 text-xs font-medium
                bg-blue-600 text-white
                rounded hover:bg-blue-700
                transition-colors duration-200
                flex items-center space-x-1
              "
            >
              <CheckIcon className="h-3 w-3" />
              <span>Segna come letta</span>
            </button>
            <button
              onClick={() => {
                if (window.confirm('Sei sicuro di voler cancellare questa notifica?')) {
                  onClose();
                }
              }}
              className="
                px-3 py-1 text-xs font-medium
                bg-red-600 text-white
                rounded hover:bg-red-700
                transition-colors duration-200
                flex items-center space-x-1
              "
            >
              <XMarkIcon className="h-3 w-3" />
              <span>Cancella</span>
            </button>
          </div>
        </div>
      </div>

      {/* Barra di progresso per auto-close */}
      {autoClose && duration > 0 && (
        <div className="
          absolute bottom-0 left-0 right-0
          h-1 bg-gray-200 dark:bg-gray-700
          rounded-b-lg overflow-hidden
        ">
          <div 
            className="
              h-full bg-blue-600
              transition-all duration-1000 ease-linear
            "
            style={{
              width: '100%',
              animation: `shrink ${duration}ms linear forwards`
            }}
          />
        </div>
      )}

      {/* CSS per animazione barra di progresso */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default NotificationPopup;
